import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Badge,
  Button,
  DataTable,
  Input,
  Label,
  Switch,
  Tag,
  type ColumnDef,
} from "@diametral/design-system/react";

import { useProjects } from "../api/projects";
import type { ProjectStatus, ProjectSummary } from "../api/types";
import { MyStatusTag, ProjectStatusTag } from "../components/Badges";
import { ButtonLink } from "../components/ButtonLink";
import { Page } from "../components/Page";
import { names } from "../components/PersonCell";
import { RemovableTag } from "../components/RemovableTag";
import { SelectField } from "../components/SelectField";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/States";
import { TrlMeter } from "../components/TrlMeter";
import { useCurrentUser } from "../lib/currentUser";
import { t, useTranslation } from "../lib/i18n";
import { PROJECT_STATUS } from "../lib/labels";
import { TRL_LEVELS } from "../lib/reference";
import { useDebounced } from "../lib/useDebounced";

type StatusFilter = ProjectStatus | "all";

function statusOptions() {
  return [
    { value: "active", label: t("Active projects") },
    { value: "paused", label: PROJECT_STATUS.paused.label },
    { value: "closed", label: PROJECT_STATUS.closed.label },
    { value: "all", label: t("All statuses") },
  ];
}

function trlOptions() {
  return [
    { value: "", label: t("All TRLs") },
    ...TRL_LEVELS.slice(1).map((level) => ({ value: String(level), label: `TRL ≥ ${level}` })),
  ];
}

/** Technologies of the listed projects, most frequent first: the options of the facet. */
export function technologyFacet(projects: ProjectSummary[]): Array<{ value: string; count: number }> {
  const counts = new Map<string, number>();
  for (const project of projects) {
    for (const tech of project.technologies) counts.set(tech, (counts.get(tech) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, "fr"));
}

const MAX_TECH_TAGS = 4;

export const projectColumns: ColumnDef<ProjectSummary, unknown>[] = [
  {
    accessorKey: "title",
    header: () => t("Project"),
    cell: ({ row }) => (
      <div className="ada-stack ada-stack--tight">
        <Link className="ada-link" to={`/projects/${row.original.slug}`}>
          {row.original.title}
        </Link>
        <span className="ada-cluster">
          {row.original.status !== "active" ? <ProjectStatusTag status={row.original.status} /> : null}
          <span className="ada-faint">{t("{{count}} streams", { count: row.original.stream_count })}</span>
        </span>
      </div>
    ),
  },
  {
    accessorKey: "trl",
    header: "TRL",
    cell: ({ row }) => <TrlMeter trl={row.original.trl} label={row.original.trl_label} />,
  },
  {
    id: "technologies",
    header: () => t("Technologies"),
    enableSorting: false,
    cell: ({ row }) => {
      const techs = row.original.technologies;
      if (!techs.length) return <span className="ada-faint">—</span>;
      return (
        <span className="ada-cluster">
          {techs.slice(0, MAX_TECH_TAGS).map((tech) => (
            <Tag key={tech}>{tech}</Tag>
          ))}
          {techs.length > MAX_TECH_TAGS ? (
            <span className="ada-faint" title={techs.slice(MAX_TECH_TAGS).join(", ")}>
              +{techs.length - MAX_TECH_TAGS}
            </span>
          ) : null}
        </span>
      );
    },
  },
  {
    accessorKey: "member_count",
    header: () => t("Headcount"),
    cell: ({ row }) => <span className="ds-numeric">{row.original.member_count}</span>,
  },
  {
    id: "tlpo",
    header: "TL / PO",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="ada-stack ada-stack--tight">
        <span>
          <span className="ada-faint">TL </span>
          {names(row.original.tech_leads)}
        </span>
        <span>
          <span className="ada-faint">PO </span>
          {names(row.original.product_owners)}
        </span>
      </span>
    ),
  },
  {
    accessorKey: "my_status",
    header: () => t("My status"),
    cell: ({ row }) => <MyStatusTag status={row.original.my_status} />,
  },
];

export function ProjectsPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as StatusFilter | null) ?? "active";
  const technology = params.get("technology") ?? "";
  const trlMin = params.get("trl") ?? "";
  const mine = params.get("mine") === "1";
  const [search, setSearch] = useState(params.get("q") ?? "");
  const q = useDebounced(search.trim(), 300);

  function update(key: string, value: string) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  useEffect(() => {
    if ((params.get("q") ?? "") !== q) update("q", q);
    // Only the settled search text drives the URL.
  }, [q]);

  // The technology filter is applied here rather than by the API, so that the facet keeps
  // offering every technology of the other filters' results (with counts).
  const projects = useProjects({ status, q: q || undefined, trl_min: Number(trlMin) || undefined, mine });
  const facet = useMemo(() => technologyFacet(projects.data ?? []), [projects.data]);
  const rows = useMemo(
    () => (projects.data ?? []).filter((p) => !technology || p.technologies.includes(technology)),
    [projects.data, technology],
  );

  const techOptions = [
    { value: "", label: t("All technologies") },
    ...facet.map((f) => ({ value: f.value, label: `${f.value} (${f.count})` })),
    // A technology from the URL that the current results do not carry stays selectable.
    ...(technology && !facet.some((f) => f.value === technology) ? [{ value: technology, label: technology }] : []),
  ];
  const filtered = Boolean(q || technology || trlMin || mine || status !== "active");

  function clearAll() {
    setSearch("");
    setParams(new URLSearchParams(), { replace: true });
  }

  return (
    <Page
      kicker={t("Internal projects")}
      title={t("Projects")}
      description={t("Internal projects, their streams and their teams. Ask to join a project from its page.")}
      actions={
        me.is_dar ? (
          <ButtonLink variant="primary" to="/admin/projects?new=1">
            {t("New project")}
          </ButtonLink>
        ) : undefined
      }
    >
      <div className="ds-block-filter-bar-01 ada-filters" role="search" aria-label={t("Filter projects")}>
        <Input
          className="ada-filters__search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("Search titles and descriptions…")}
          aria-label={t("Search for a project")}
        />
        <SelectField label={t("Status")} value={status} onChange={(v) => update("status", v === "active" ? "" : v)} options={statusOptions()} />
        <SelectField label={t("Technology")} value={technology} onChange={(v) => update("technology", v)} options={techOptions} />
        <SelectField label={t("Minimum TRL")} value={trlMin} onChange={(v) => update("trl", v)} options={trlOptions()} />
        <span className="ada-cluster">
          <Switch id="projects-mine" checked={mine} onCheckedChange={(checked: boolean) => update("mine", checked ? "1" : "")} />
          <Label htmlFor="projects-mine">{t("My projects")}</Label>
        </span>
      </div>

      {filtered ? (
        <div className="ada-cluster" aria-label={t("Active filters")}>
          {q ? (
            <RemovableTag removeLabel={t("Remove the search")} onRemove={() => setSearch("")}>
              {t("“{{text}}”", { text: q })}
            </RemovableTag>
          ) : null}
          {status !== "active" ? (
            <RemovableTag removeLabel={t("Back to active projects")} onRemove={() => update("status", "")}>
              {status === "all" ? t("All statuses") : PROJECT_STATUS[status].label}
            </RemovableTag>
          ) : null}
          {technology ? (
            <RemovableTag removeLabel={t("Remove {{name}}", { name: technology })} onRemove={() => update("technology", "")}>
              {technology}
            </RemovableTag>
          ) : null}
          {trlMin ? (
            <RemovableTag removeLabel={t("Remove the minimum TRL")} onRemove={() => update("trl", "")}>
              TRL ≥ {trlMin}
            </RemovableTag>
          ) : null}
          {mine ? (
            <RemovableTag removeLabel={t("Remove “My projects”")} onRemove={() => update("mine", "")}>
              {t("My projects")}
            </RemovableTag>
          ) : null}
          <Badge variant="secondary">{t("{{count}} projects", { count: rows.length })}</Badge>
          <Button size="sm" variant="ghost" onClick={clearAll}>
            {t("Clear filters")}
          </Button>
        </div>
      ) : null}

      {projects.isPending ? (
        <LoadingBlock lines={6} label={t("Loading projects…")} />
      ) : projects.isError ? (
        <ErrorBlock error={projects.error} onRetry={() => void projects.refetch()} />
      ) : (
        <div className="ada-table-wrap">
          <DataTable
            columns={projectColumns}
            data={rows}
            rowKey={(p) => p.slug}
            defaultSort={[{ id: "title", desc: false }]}
            emptyMessage={
              <EmptyBlock
                icon="folder"
                title={filtered ? t("No matching project") : t("No active project")}
                description={filtered ? t("Widen the filters or clear them.") : t("Projects created by the DAR will appear here.")}
              />
            }
          />
        </div>
      )}
    </Page>
  );
}
